/**
 * Achievement Celebration Component
 * Modal that displays when user earns a new badge
 */

'use client';

import { useEffect, useState } from 'react';
import { X, Share2, Twitter } from 'lucide-react';
import BadgeDisplay, { Badge } from './BadgeDisplay';

interface AchievementCelebrationProps {
  badge: Badge | null;
  onClose: () => void;
  onShare?: (platform: string) => void;
}

export default function AchievementCelebration({
  badge,
  onClose,
  onShare,
}: AchievementCelebrationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [confetti, setConfetti] = useState<Array<{ id: number; x: number; delay: number }>>([]);

  useEffect(() => {
    if (badge) {
      // Show modal with animation
      requestAnimationFrame(() => setIsVisible(true));

      // Generate confetti particles
      const particles = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
      }));
      setConfetti(particles);

      // Auto-close after 10 seconds
      const timer = setTimeout(() => {
        handleClose();
      }, 10000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [badge]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation
  };

  const handleShare = (platform: string) => {
    if (!badge) return;

    const shareText = `I just earned the "${badge.name}" badge on The Art of CTO! ${badge.icon}`;
    const shareUrl = 'https://theartofcto.com';

    if (platform === 'twitter') {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
        '_blank'
      );
    } else if (platform === 'linkedin') {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
        '_blank'
      );
    }

    onShare?.(platform);
  };

  if (!badge) return null;

  const tierMessages = {
    bronze: 'Great start!',
    silver: 'Impressive progress!',
    gold: 'Outstanding achievement!',
    platinum: 'Legendary status!',
  };

  return (
    <div
      className={`
        fixed inset-0 z-50 flex items-center justify-center
        transition-opacity duration-300
        ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}
      `}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Confetti */}
      {confetti.map((particle) => (
        <div
          key={particle.id}
          className="absolute top-0 pointer-events-none animate-confetti"
          style={{
            left: `${particle.x}%`,
            animationDelay: `${particle.delay}s`,
          }}
        >
          <div
            className="w-3 h-3 rounded-full"
            style={{
              background: ['#E89841', '#E8D5B7', '#4ECDC4', '#F38181', '#AA96DA'][
                particle.id % 5
              ],
            }}
          />
        </div>
      ))}

      {/* Modal */}
      <div
        className={`
          relative bg-surface rounded-2xl border border-border shadow-2xl
          max-w-md w-full mx-4 p-8 text-center
          transition-transform duration-300
          ${isVisible ? 'scale-100' : 'scale-95'}
        `}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-foreground-tertiary hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Celebration Header */}
        <div className="text-6xl mb-4 animate-bounce">🎉</div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Achievement Unlocked!</h2>
        <p className="text-foreground-secondary mb-6">{tierMessages[badge.tier]}</p>

        {/* Badge Display */}
        <div className="flex justify-center mb-6">
          <BadgeDisplay badge={badge} size="lg" showTooltip={false} />
        </div>

        {/* Badge Info */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-foreground mb-2">{badge.name}</h3>
          <p className="text-foreground-secondary">{badge.description}</p>
        </div>

        {/* Share Buttons */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <button
            onClick={() => handleShare('twitter')}
            className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 border border-[#1DA1F2]/30 text-[#1DA1F2] rounded-lg transition-colors"
          >
            <Twitter className="w-4 h-4" />
            Share
          </button>
          <button
            onClick={() => handleShare('linkedin')}
            className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 border border-[#0A66C2]/30 text-[#0A66C2] rounded-lg transition-colors"
          >
            <Share2 className="w-4 h-4" />
            LinkedIn
          </button>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleClose}
          className="w-full py-3 bg-accent hover:bg-accent-hover text-accent-foreground font-semibold rounded-lg transition-colors"
        >
          Continue
        </button>
      </div>

      <style jsx>{`
        @keyframes confetti {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
